<template>
  <v-row>
    <v-col cols="12">
      <v-card class="rec-card" flat>
        <!-- HEADER BAND -->
        <div class="rec-header pl-base-bg">
          <div class="rec-header-icon">
            <v-icon color="#fff" size="22">mdi-file-compare</v-icon>
          </div>
          <div>
            <div class="rec-title">GSTR-2A/2B Reconciliation</div>
            <div class="rec-subtitle">Match claimed ITC against what your vendors actually filed</div>
          </div>
        </div>

        <div class="pa-6">
          <v-alert type="info" outlined dense border="left" class="rec-disclaimer">
            Covers B2B and CDNR rows from your GSTR-2A/2B Excel export.
            Amendments, ISD credit and imports are not covered. Always
            verify totals with your accountant before filing.
          </v-alert>

          <div class="rec-filter-bar">
            <v-row dense align="center">
              <v-col cols="12" md="3">
                <v-menu
                  v-model="summaryPeriodMenu"
                  :close-on-content-click="false"
                  offset-y
                  max-width="290"
                  min-width="290"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="formattedSummaryPeriod"
                      label="Filing Period (blank = all)"
                      readonly
                      clearable
                      outlined
                      dense
                      hide-details
                      v-bind="attrs"
                      v-on="on"
                      @click:clear="summaryPeriod = null"
                    />
                  </template>
                  <v-date-picker
                    v-model="summaryPeriod"
                    type="month"
                    @input="summaryPeriodMenu = false"
                  />
                </v-menu>
              </v-col>

              <v-col cols="12" md="9" class="rec-actions">
                <v-btn class="rec-import-btn" :loading="loadingSummary" @click="runReconciliation">
                  <v-icon left size="16">mdi-play-circle-outline</v-icon>
                  Run Reconciliation
                </v-btn>

                <v-btn
                  outlined
                  color="#1d6f42"
                  class="ml-2"
                  :disabled="!summary"
                  @click="downloadExcel"
                >
                  <v-icon left size="16" color="#1d6f42">mdi-file-excel-box</v-icon>
                  Excel
                </v-btn>

                <v-btn class="ml-2" outlined color="#0a5a90" @click="importDialog = true">
                  <v-icon left size="16">mdi-upload</v-icon>
                  Import 2A/2B
                </v-btn>
              </v-col>
            </v-row>
          </div>

          <template v-if="summary">
            <v-row dense class="mb-4">
              <v-col cols="6" md="3">
                <v-card
                  class="rec-kpi-card rec-kpi-card--matched pa-4"
                  flat
                  @click="selectedBucket = 'matched'"
                  :class="{ 'rec-kpi-card--active': selectedBucket === 'matched' }"
                >
                  <div class="rec-kpi-label">Matched</div>
                  <div class="rec-kpi-value">{{ summary.summary.matched.count }}</div>
                  <div class="rec-kpi-sub">ITC {{ formatINR(summary.summary.matched.itc) }}</div>
                </v-card>
              </v-col>
              <v-col cols="6" md="3">
                <v-card
                  class="rec-kpi-card rec-kpi-card--mismatched pa-4"
                  flat
                  @click="selectedBucket = 'mismatched'"
                  :class="{ 'rec-kpi-card--active': selectedBucket === 'mismatched' }"
                >
                  <div class="rec-kpi-label">Mismatched</div>
                  <div class="rec-kpi-value">{{ summary.summary.mismatched.count }}</div>
                  <div class="rec-kpi-sub">ITC {{ formatINR(summary.summary.mismatched.itc) }}</div>
                </v-card>
              </v-col>
              <v-col cols="6" md="3">
                <v-card
                  class="rec-kpi-card rec-kpi-card--risk pa-4"
                  flat
                  @click="selectedBucket = 'onlyInBooks'"
                  :class="{ 'rec-kpi-card--active': selectedBucket === 'onlyInBooks' }"
                >
                  <div class="rec-kpi-label">Only in Books (ITC at Risk)</div>
                  <div class="rec-kpi-value">{{ summary.summary.onlyInBooks.count }}</div>
                  <div class="rec-kpi-sub">ITC {{ formatINR(summary.summary.onlyInBooks.itc) }}</div>
                </v-card>
              </v-col>
              <v-col cols="6" md="3">
                <v-card
                  class="rec-kpi-card rec-kpi-card--opportunity pa-4"
                  flat
                  @click="selectedBucket = 'onlyInReturn'"
                  :class="{ 'rec-kpi-card--active': selectedBucket === 'onlyInReturn' }"
                >
                  <div class="rec-kpi-label">Only in Return (Unclaimed)</div>
                  <div class="rec-kpi-value">{{ summary.summary.onlyInReturn.count }}</div>
                  <div class="rec-kpi-sub">ITC {{ formatINR(summary.summary.onlyInReturn.itc) }}</div>
                </v-card>
              </v-col>
            </v-row>

            <div class="rec-section-title">{{ bucketLabels[selectedBucket] }}</div>
            <v-simple-table class="rec-table mb-6">
              <thead>
                <tr>
                  <th>Vendor</th>
                  <th>GSTIN</th>
                  <th>Bill / Invoice #</th>
                  <th>Bill Date</th>
                  <th class="text-right">Books Tax</th>
                  <th>Filed Invoice #</th>
                  <th>Filed Date</th>
                  <th class="text-right">Filed Tax</th>
                  <th class="text-right">Delta</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, i) in selectedRows" :key="i">
                  <td>{{ row.vendorName || "-" }}</td>
                  <td>{{ row.vendorGstin || "-" }}</td>
                  <td>{{ row.billNumber || "-" }}</td>
                  <td>{{ formatDate(row.billDate) }}</td>
                  <td class="text-right">{{ row.purchaseTax !== null ? formatINR(row.purchaseTax) : "-" }}</td>
                  <td>{{ row.invoiceNumber || "-" }}</td>
                  <td>{{ formatDate(row.invoiceDate) }}</td>
                  <td class="text-right">{{ row.filedTax !== null ? formatINR(row.filedTax) : "-" }}</td>
                  <td class="text-right">{{ row.delta !== null ? formatINR(row.delta) : "-" }}</td>
                </tr>
                <tr v-if="!selectedRows.length">
                  <td colspan="9" class="rec-empty-cell">No rows in this bucket</td>
                </tr>
              </tbody>
            </v-simple-table>
          </template>

          <div v-else class="rec-empty-state mb-6">
            Click "Run Reconciliation" to match your Purchases against imported GSTR-2A/2B data.
          </div>

          <!-- IMPORT HISTORY -->
          <div class="rec-section-title">Import History</div>
          <v-simple-table class="rec-table mb-6">
            <thead>
              <tr>
                <th>Source</th>
                <th>Filing Period</th>
                <th>File</th>
                <th class="text-right">Rows</th>
                <th>Imported On</th>
                <th class="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="batch in importHistory" :key="batch._id">
                <td>{{ batch.source }}</td>
                <td>{{ batch.filingPeriod }}</td>
                <td>{{ batch.fileName || "-" }}</td>
                <td class="text-right">{{ batch.rowCount }}</td>
                <td>{{ formatDate(batch.createdAt) }}</td>
                <td class="text-right">
                  <v-btn icon small @click="confirmDeleteBatch(batch)">
                    <v-icon size="18" color="#d93025">mdi-delete-outline</v-icon>
                  </v-btn>
                </td>
              </tr>
              <tr v-if="!importHistory.length">
                <td colspan="6" class="rec-empty-cell">No imports yet</td>
              </tr>
            </tbody>
          </v-simple-table>
        </div>
      </v-card>
    </v-col>

    <!-- IMPORT DIALOG -->
    <v-dialog v-model="importDialog" max-width="720" persistent>
      <v-card>
        <v-card-title>Import GSTR-2A/2B</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" sm="4">
              <v-select
                v-model="importForm.source"
                :items="['2A', '2B']"
                label="Source"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-menu
                v-model="periodMenu"
                :close-on-content-click="false"
                offset-y
                max-width="290"
                min-width="290"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="formattedPeriod"
                    label="Filing Period"
                    readonly
                    outlined
                    dense
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="importForm.filingPeriod"
                  type="month"
                  @input="periodMenu = false"
                />
              </v-menu>
            </v-col>
            <v-col cols="12" sm="4">
              <v-file-input
                v-model="file"
                label="Excel File"
                accept=".xlsx,.xls"
                outlined
                dense
                prepend-icon=""
                prepend-inner-icon="mdi-paperclip"
                @change="onFileSelected"
              />
            </v-col>
          </v-row>

          <div v-if="parsing" class="rec-empty-state">Parsing file...</div>

          <template v-if="parsedResult">
            <v-alert
              v-if="parsedResult.errors.length"
              type="warning"
              outlined
              dense
              class="mb-3"
            >
              {{ parsedResult.errors.length }} row(s) skipped due to errors
              (malformed GSTIN, missing invoice number/date). They will not
              be imported.
            </v-alert>

            <div class="rec-preview-summary mb-3">
              <strong>{{ parsedResult.rows.length }}</strong> row(s) ready to import
              from sheet(s): {{ parsedResult.sheetsFound.join(", ") || "none found" }}
            </div>

            <v-simple-table dense class="rec-preview-table" v-if="parsedResult.rows.length">
              <thead>
                <tr>
                  <th>GSTIN</th>
                  <th>Supplier</th>
                  <th>Invoice #</th>
                  <th>Date</th>
                  <th class="text-right">Taxable Value</th>
                  <th class="text-right">Total Tax</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, i) in previewRows" :key="i">
                  <td>{{ row.supplierGstin }}</td>
                  <td>{{ row.supplierName }}</td>
                  <td>{{ row.invoiceNumber }}</td>
                  <td>{{ row.invoiceDate }}</td>
                  <td class="text-right">{{ formatINR(row.taxableValue) }}</td>
                  <td class="text-right">{{ formatINR(row.igst + row.cgst + row.sgst) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
            <div v-if="parsedResult.rows.length > previewRows.length" class="rec-preview-more">
              + {{ parsedResult.rows.length - previewRows.length }} more row(s)
            </div>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeImportDialog">Cancel</v-btn>
          <v-btn
            class="rec-import-btn"
            :disabled="!canImport"
            :loading="importing"
            @click="doImport"
          >
            Import
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DELETE CONFIRM -->
    <v-dialog v-model="deleteDialog" max-width="420">
      <v-card>
        <v-card-title>Delete Import Batch?</v-card-title>
        <v-card-text>
          This removes all {{ batchToDelete?.rowCount }} imported row(s) for
          {{ batchToDelete?.source }} {{ batchToDelete?.filingPeriod }}. This can't be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" :loading="deleting" @click="doDeleteBatch">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import { parseGstr2Workbook } from "@/utils/gstr2-parse";
import { downloadMultiSheetWorkbook } from "@/utils/xlsx-export";

const BUCKET_LABELS = {
  matched: "Matched",
  mismatched: "Mismatched",
  onlyInBooks: "Only in Books (ITC at Risk)",
  onlyInReturn: "Only in Return (Unclaimed ITC)",
};

export default {
  name: "ReconciliationPage",

  data() {
    return {
      importDialog: false,
      deleteDialog: false,
      periodMenu: false,

      file: null,
      parsing: false,
      parsedResult: null,
      importing: false,
      deleting: false,
      batchToDelete: null,

      importForm: {
        source: "2A",
        filingPeriod: new Date().toISOString().slice(0, 7),
      },

      summaryPeriod: null,
      summaryPeriodMenu: false,
      loadingSummary: false,
      selectedBucket: "matched",
      bucketLabels: BUCKET_LABELS,
    };
  },

  computed: {
    importHistory() {
      return this.$store.state.reconciliation.importHistory;
    },

    summary() {
      return this.$store.state.reconciliation.summary;
    },

    selectedRows() {
      return this.summary ? this.summary.rows[this.selectedBucket] : [];
    },

    formattedPeriod() {
      if (!this.importForm.filingPeriod) return "";
      const d = new Date(`${this.importForm.filingPeriod}-01`);
      return d.toLocaleString("default", { month: "long", year: "numeric" });
    },

    formattedSummaryPeriod() {
      if (!this.summaryPeriod) return "";
      const d = new Date(`${this.summaryPeriod}-01`);
      return d.toLocaleString("default", { month: "long", year: "numeric" });
    },

    previewRows() {
      return this.parsedResult ? this.parsedResult.rows.slice(0, 20) : [];
    },

    canImport() {
      return !!(this.parsedResult && this.parsedResult.rows.length && !this.importing);
    },
  },

  created() {
    this.loadHistory();
  },

  methods: {
    formatINR(value) {
      if (value === null || value === undefined) return "0";
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
      }).format(value);
    },

    formatDate(value) {
      if (!value) return "-";
      const d = new Date(value);
      return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
    },

    async loadHistory() {
      try {
        await this.$store.dispatch("reconciliation/getImportHistory");
      } catch (err) {
        console.error(err);
        this.$toast?.error("Failed to load import history");
      }
    },

    async onFileSelected(file) {
      this.parsedResult = null;
      if (!file) return;

      this.parsing = true;
      try {
        const arrayBuffer = await file.arrayBuffer();
        this.parsedResult = parseGstr2Workbook(arrayBuffer);

        if (!this.parsedResult.rows.length) {
          this.$toast?.error("No B2B/CDNR rows found in this file");
        }
      } catch (err) {
        console.error(err);
        this.$toast?.error("Failed to parse file — is this a valid GSTR-2A/2B export?");
        this.parsedResult = null;
      } finally {
        this.parsing = false;
      }
    },

    async doImport() {
      this.importing = true;
      try {
        const result = await this.$store.dispatch("reconciliation/importReturns", {
          source: this.importForm.source,
          filingPeriod: this.importForm.filingPeriod,
          fileName: this.file?.name,
          rows: this.parsedResult.rows,
        });

        this.$toast?.success(`Imported ${result.imported} row(s) successfully`);
        this.closeImportDialog();
      } catch (err) {
        console.error(err);
        this.$toast?.error(err?.response?.data?.message || "Failed to import");
      } finally {
        this.importing = false;
      }
    },

    closeImportDialog() {
      this.importDialog = false;
      this.file = null;
      this.parsedResult = null;
    },

    confirmDeleteBatch(batch) {
      this.batchToDelete = batch;
      this.deleteDialog = true;
    },

    async doDeleteBatch() {
      this.deleting = true;
      try {
        await this.$store.dispatch("reconciliation/deleteImportBatch", this.batchToDelete._id);
        this.$toast?.success("Import batch deleted");
        this.deleteDialog = false;
      } catch (err) {
        console.error(err);
        this.$toast?.error(err?.response?.data?.message || "Failed to delete batch");
      } finally {
        this.deleting = false;
      }
    },

    async runReconciliation() {
      this.loadingSummary = true;
      try {
        await this.$store.dispatch("reconciliation/getSummary", {
          filingPeriod: this.summaryPeriod || undefined,
        });
      } catch (err) {
        console.error(err);
        this.$toast?.error(err?.response?.data?.message || "Failed to run reconciliation");
      } finally {
        this.loadingSummary = false;
      }
    },

    downloadExcel() {
      const s = this.summary;

      const rowToArray = (row) => [
        row.vendorName || "-",
        row.vendorGstin || "-",
        row.billNumber || "-",
        row.billDate ? this.formatDate(row.billDate) : "-",
        row.purchaseTax !== null ? Number(row.purchaseTax) : "",
        row.invoiceNumber || "-",
        row.invoiceDate ? this.formatDate(row.invoiceDate) : "-",
        row.filedTax !== null ? Number(row.filedTax) : "",
        row.delta !== null ? Number(row.delta) : "",
      ];

      const headers = ["Vendor", "GSTIN", "Bill/Invoice #", "Bill Date", "Books Tax", "Filed Invoice #", "Filed Date", "Filed Tax", "Delta"];
      const numberColumns = [4, 7, 8];
      const period = this.summaryPeriod || "All Periods";

      downloadMultiSheetWorkbook(
        Object.entries(this.bucketLabels).map(([key, name]) => ({
          name,
          title: name,
          subtitle: period,
          headers,
          rows: s.rows[key].map(rowToArray),
          columnWidths: [20, 18, 16, 12, 14, 16, 12, 14, 12],
          numberColumns,
        })),
        `reconciliation-${period}.xlsx`,
      );
    },
  },
};
</script>

<style scoped>
.rec-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.rec-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
}

.rec-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.rec-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.rec-subtitle {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.rec-disclaimer {
  margin-bottom: 20px;
  font-size: 13px;
}

.rec-filter-bar {
  margin-bottom: 20px;
}

.rec-actions {
  display: flex;
  align-items: center;
}

.rec-import-btn {
  background: var(--pl-base-bg-gradient) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.rec-kpi-card {
  border-radius: 0;
  color: #fff;
  box-shadow: 0 6px 16px rgba(15, 30, 50, 0.12);
  cursor: pointer;
  transition: transform 0.15s ease;
  border: 3px solid transparent;
}

.rec-kpi-card:hover {
  transform: translateY(-2px);
}

.rec-kpi-card--active {
  border-color: rgba(255, 255, 255, 0.7);
}

.rec-kpi-card--matched {
  background: linear-gradient(135deg, #0d8478 0%, #15a355 100%);
}
.rec-kpi-card--mismatched {
  background: linear-gradient(135deg, #92400e 0%, #d97706 100%);
}
.rec-kpi-card--risk {
  background: linear-gradient(135deg, #7a1f1f 0%, #b23a3a 100%);
}
.rec-kpi-card--opportunity {
  background: linear-gradient(135deg, #052f5e 0%, #0a5a90 100%);
}

.rec-kpi-label {
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
}

.rec-kpi-value {
  font-size: 22px;
  font-weight: 700;
  margin-top: 4px;
  letter-spacing: -0.01em;
}

.rec-kpi-sub {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.85);
  margin-top: 4px;
}

.rec-section-title {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
  margin-bottom: 10px;
}

.rec-table,
.rec-preview-table {
  border: 1px solid #eef1f5;
  border-radius: 0;
}

.rec-empty-cell {
  text-align: center;
  color: #94a3b8;
  padding: 16px 0 !important;
}

.rec-empty-state {
  text-align: center;
  padding: 24px 0;
  color: #94a3b8;
  font-size: 14px;
}

.rec-preview-summary {
  font-size: 13px;
  color: #334155;
}

.rec-preview-more {
  font-size: 12px;
  color: #94a3b8;
  margin-top: 6px;
  text-align: center;
}
</style>
